/*
  Flag picker for the quick flag addon (sections/ss-product-addons-2.liquid).

  Replaces the addon's native country select and tick box with one control:
  a button showing the chosen flag that opens a searchable list of countries
  with their flags. Choosing a country selects it in the addon's own select
  and ticks its checkbox; "No flag" unticks it. Both still fire their change
  events, so the addon's add-to-cart fields are built exactly as before.

  The list sits in a <template> inside the picker and is moved to <body> when
  opened, because the addon card lives in a Swiper slide whose transform and
  overflow would clip it. Wide screens get a dropdown under the button; narrow
  screens get a sheet from the bottom of the screen.

  Popular countries: the shopper's own country (Shopify's localization), then
  a fixed shortlist. Countries are matched by the code in the flag artwork's
  file name (data-code), which is not translated.

  Loaded as an external file: the storefront's instant-loader app never runs
  inline scripts it swaps into the page.
*/
(() => {
  if (customElements.get("sk-flag-picker")) return;

  const POPULAR = ["TH", "US", "GB", "AU", "DE", "FR"];
  const POPULAR_SIZE = 6;
  const WIDE = window.matchMedia("(min-width: 750px)");

  // Extra words the search accepts for a country, keyed by the code in its
  // flag artwork's file name (data-code). The code itself always counts too.
  // A country added later without an entry here is still found by its name.
  // No "england": the Union Jack is not the English flag.
  const ALIASES = {
    US: ["usa", "america", "united states of america", "states"],
    GB: ["uk", "britain", "great britain"],
    "GB-SCT": ["sco", "sct"],
    AU: ["aus", "aussie"],
    NZ: ["aotearoa", "kiwi"],
    CA: ["can"],
    DE: ["ger", "deutschland"],
    FR: ["fra"],
    IT: ["ita", "italia"],
    BR: ["bra", "brasil"],
    CH: ["swiss", "sui", "schweiz", "suisse", "svizzera"],
    SE: ["swe", "sverige"],
    DK: ["den", "danmark"],
    NL: ["holland", "ned", "nederland"],
    CN: ["chn", "prc"],
    JP: ["jpn", "nippon", "nihon"],
    KR: ["korea", "kor", "rok"],
    SG: ["sgp"],
    MY: ["mas", "mys"],
    TW: ["roc", "tpe"],
    TH: ["tha", "thai", "siam"],
    HU: ["hun", "magyarorszag"],
    PH: ["phi", "phl", "pilipinas"],
    PL: ["pol", "polska"],
    TR: ["tur", "turkiye"],
    IL: ["isr"],
    CZ: ["cze", "czechia", "cesko"],
    CO: ["col"],
    VE: ["ven"],
    SL: ["sle"],
    BELARUS_WRW: ["by", "blr"],
    IE: ["irl", "eire"],
    AT: ["aut", "osterreich"],
    BE: ["bel", "belgie", "belgique"],
    MM: ["mya", "burma"],
    UA: ["ukr", "ukraina"],
    BJ: ["ben"],
  };

  // Lower case, with accents and punctuation dropped, so "U.S.A." and
  // "Türkiye" match "usa" and "turkiye".
  const normalize = (text) =>
    String(text || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .replace(/[^\p{L}\p{N} ]+/gu, "")
      .replace(/\s+/g, " ")
      .trim();

  // How well an option matches a query: 0 an exact name, code or alias,
  // 1 the name starts with it, 2 a word in the name or an alias starts with
  // it, 3 the name contains it; -1 no match. So "uk" lists the United
  // Kingdom before Ukraine, and "us" the United States before Australia.
  function rank(option, query) {
    const name = normalize(option.dataset.name);
    const code = normalize(option.dataset.code);
    const aliases = (ALIASES[option.dataset.code] || []).map(normalize);
    if (name === query || code === query || aliases.includes(query)) return 0;
    if (name.startsWith(query)) return 1;
    if (name.split(" ").some((word) => word.startsWith(query))) return 2;
    if (aliases.some((alias) => alias.startsWith(query))) return 2;
    if (name.includes(query)) return 3;
    return -1;
  }

  class SkFlagPicker extends HTMLElement {
    connectedCallback() {
      if (this.trigger) return;
      this.trigger = this.querySelector(".sk-flag-picker__trigger");
      this.triggerFlag = this.querySelector(".sk-flag-picker__trigger-flag");
      this.triggerLabel = this.querySelector(".sk-flag-picker__trigger-label");
      this.template = this.querySelector("template");
      const index = this.dataset.addonIndex;
      this.select = document.querySelector(`[data-addons-variant-select--${index}]`);
      this.input = document.querySelector(`[data-addons-input--${index}]`);
      if (!this.trigger || !this.template || !this.select || !this.input) return;

      this.onDocumentPointer = this.onDocumentPointer.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      this.trigger.addEventListener("click", () => (this.overlay ? this.close() : this.open()));
      // Keep the button in step with anything else that changes the addon
      // (a sold-out country unticking it, the browser restoring a form).
      this.input.addEventListener("change", () => this.render());
      this.select.addEventListener("change", () => this.render());
      this.render();
    }

    disconnectedCallback() {
      this.close(false);
    }

    // The country the addon will add, or null for "No flag".
    current() {
      if (!this.input.checked || this.input.disabled) return null;
      return this.template.content.querySelector(
        `.sk-flag-picker__option[data-variant-id="${this.select.value}"]`,
      );
    }

    render() {
      const option = this.current();
      const flag = option && option.querySelector("img.sk-flag-picker__flag");
      this.triggerLabel.textContent = option
        ? option.querySelector(".sk-flag-picker__name").textContent.trim()
        : this.dataset.chooseLabel;
      this.trigger.classList.toggle("is-empty", !option);
      if (flag) {
        this.triggerFlag.src = flag.currentSrc || flag.src;
        this.triggerFlag.hidden = false;
      } else {
        this.triggerFlag.hidden = true;
        this.triggerFlag.removeAttribute("src");
      }
    }

    choose(variantId) {
      if (variantId) {
        if (this.select.value !== variantId) {
          this.select.value = variantId;
          this.select.dispatchEvent(new Event("change", { bubbles: true }));
        }
        if (!this.input.disabled && !this.input.checked) {
          this.input.checked = true;
          this.input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      } else if (this.input.checked) {
        this.input.checked = false;
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      this.render();
    }

    open() {
      if (this.overlay) return;
      const fragment = this.template.content.cloneNode(true);
      this.overlay = fragment.querySelector(".sk-flag-picker__overlay");
      this.panel = this.overlay.querySelector(".sk-flag-picker__panel");
      this.search = this.overlay.querySelector(".sk-flag-picker__search");
      this.fillPopular();
      this.markSelected();

      this.overlay.addEventListener("click", (event) => {
        const option = event.target.closest(".sk-flag-picker__option");
        if (option && !option.disabled) {
          this.choose(option.dataset.variantId);
          this.close();
          return;
        }
        if (event.target.closest(".sk-flag-picker__close") || event.target === this.overlay) {
          this.close();
        }
      });
      this.search.addEventListener("input", () => this.filter());

      this.overlay.classList.toggle("is-sheet", !WIDE.matches);
      document.body.appendChild(this.overlay);
      this.position();
      if (!WIDE.matches) document.documentElement.classList.add("sk-flag-picker-lock");

      this.trigger.setAttribute("aria-expanded", "true");
      document.addEventListener("pointerdown", this.onDocumentPointer, true);
      document.addEventListener("keydown", this.onKey, true);
      window.addEventListener("resize", this.onViewportChange);
      window.addEventListener("scroll", this.onViewportChange, true);

      // Typing straight away on a computer; on a phone the keyboard would
      // cover the list, so focus the chosen country instead.
      const selected = this.panel.querySelector(".sk-flag-picker__option.is-selected");
      if (WIDE.matches) this.search.focus({ preventScroll: true });
      else (selected || this.panel).focus({ preventScroll: true });
      if (selected && selected.scrollIntoView) selected.scrollIntoView({ block: "nearest" });
    }

    close(restoreFocus = true) {
      if (!this.overlay) return;
      this.overlay.remove();
      this.overlay = this.panel = this.search = null;
      document.documentElement.classList.remove("sk-flag-picker-lock");
      this.trigger.setAttribute("aria-expanded", "false");
      document.removeEventListener("pointerdown", this.onDocumentPointer, true);
      document.removeEventListener("keydown", this.onKey, true);
      window.removeEventListener("resize", this.onViewportChange);
      window.removeEventListener("scroll", this.onViewportChange, true);
      if (restoreFocus) this.trigger.focus({ preventScroll: true });
    }

    fillPopular() {
      const all = Array.from(this.panel.querySelectorAll("[data-all] .sk-flag-picker__option"));
      const byCode = new Map(all.map((option) => [option.dataset.code, option]));
      const codes = [String(this.dataset.shopperCountry || "").toUpperCase(), ...POPULAR];
      const picked = [];
      for (const code of codes) {
        const option = byCode.get(code);
        if (option && !option.disabled && !picked.includes(option)) picked.push(option);
        if (picked.length === POPULAR_SIZE) break;
      }
      const list = this.panel.querySelector("[data-popular]");
      for (const option of picked) {
        const item = document.createElement("li");
        item.appendChild(option.cloneNode(true));
        list.appendChild(item);
      }
      this.panel.querySelector("[data-popular-group]").hidden = picked.length === 0;
    }

    markSelected() {
      const current = this.current();
      const id = current ? current.dataset.variantId : "";
      this.panel.querySelectorAll(".sk-flag-picker__option").forEach((option) => {
        const selected = option.dataset.variantId === id;
        option.classList.toggle("is-selected", selected);
        option.setAttribute("aria-pressed", String(selected));
      });
    }

    filter() {
      const query = normalize(this.search.value);
      const list = this.panel.querySelector("[data-all]");
      const items = Array.from(list.children);
      items.forEach((item, index) => {
        if (item.dataset.order === undefined) item.dataset.order = String(index);
      });
      let shown = 0;
      const ranked = items.map((item) => {
        const score = query ? rank(item.querySelector(".sk-flag-picker__option"), query) : 0;
        item.hidden = score < 0;
        if (score >= 0) shown += 1;
        return { item, score };
      });
      // Best matches first while searching; back to A-Z once cleared.
      ranked
        .sort((x, y) => x.score - y.score || x.item.dataset.order - y.item.dataset.order)
        .forEach(({ item }) => list.appendChild(item));

      this.panel.querySelector("[data-popular-group]").hidden =
        Boolean(query) || !this.panel.querySelector("[data-popular] li");
      this.panel.querySelector(".sk-flag-picker__option--none").hidden = Boolean(query);
      this.panel.querySelector("[data-all-heading]").hidden = Boolean(query);
      this.panel.querySelector(".sk-flag-picker__empty").hidden = shown > 0;
    }

    position() {
      // The sheet is placed by CSS; only the dropdown follows the button.
      if (!this.overlay || this.overlay.classList.contains("is-sheet")) return;
      const rect = this.trigger.getBoundingClientRect();
      const width = Math.max(rect.width, 300);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      const below = window.innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const openUp = below < 280 && above > below;
      const maxHeight = Math.min(420, openUp ? above : below);
      Object.assign(this.panel.style, {
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(200, maxHeight)}px`,
        top: openUp ? "" : `${rect.bottom + 4}px`,
        bottom: openUp ? `${window.innerHeight - rect.top + 4}px` : "",
      });
    }

    onViewportChange(event) {
      // Scrolling inside the list is fine; scrolling the page moves the
      // button away from a dropdown, so close it.
      if (event && event.type === "scroll" && this.panel && this.panel.contains(event.target)) return;
      if (this.overlay && this.overlay.classList.contains("is-sheet") && event && event.type === "scroll") return;
      this.close(false);
    }

    onDocumentPointer(event) {
      if (!this.overlay) return;
      if (this.panel.contains(event.target) || this.trigger.contains(event.target)) return;
      if (this.overlay.classList.contains("is-sheet")) return; // the backdrop click closes it
      this.close(false);
    }

    onKey(event) {
      if (!this.overlay) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const options = Array.from(this.panel.querySelectorAll(".sk-flag-picker__option")).filter(
        (option) => !option.disabled && option.offsetParent !== null,
      );
      if (!options.length) return;
      event.preventDefault();
      const at = options.indexOf(document.activeElement);
      const next =
        event.key === "ArrowDown"
          ? options[Math.min(options.length - 1, at + 1)]
          : at <= 0
            ? this.search
            : options[at - 1];
      next.focus();
      if (next.scrollIntoView) next.scrollIntoView({ block: "nearest" });
    }
  }

  customElements.define("sk-flag-picker", SkFlagPicker);
})();
